class PrioCart {
  static get element() {
    return document.querySelector('prio-option');
  }

  static get settings() {
    const element = PrioCart.element;
    if (!element) return null;

    const productId = Number(element.dataset.productId);
    const variantId = Number(element.dataset.variantId);
    if (!productId || !variantId) return null;

    return { productId, variantId };
  }

  static get sections() {
    const drawerInner = document.querySelector('cart-drawer .drawer__inner');
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    const cartLiveRegion = document.getElementById('cart-live-region-text');
    const sections = [];

    if (drawerInner) sections.push({ id: 'cart-drawer', source: '.drawer__inner', target: drawerInner });
    if (mainCartItems) {
      sections.push({
        id: mainCartItems.dataset.id,
        source: '.js-contents',
        target: mainCartItems.querySelector('.js-contents'),
      });
    }
    if (mainCartFooter) {
      sections.push({
        id: mainCartFooter.dataset.id,
        source: '.js-contents',
        target: mainCartFooter.querySelector('.js-contents'),
      });
    }
    if (cartIconBubble) sections.push({ id: 'cart-icon-bubble', source: '.shopify-section', target: cartIconBubble });
    if (cartLiveRegion) {
      sections.push({ id: 'cart-live-region-text', source: '.shopify-section', target: cartLiveRegion });
    }

    return sections.filter((section) => section.id && section.target);
  }

  static isRunning() {
    return document.documentElement.classList.contains('prio-visible');
  }

  static addWhenTestStarts() {
    if (PrioCart.pendingObserver) return;

    PrioCart.pendingObserver = new MutationObserver(() => {
      if (!PrioCart.isRunning()) return;

      PrioCart.pendingObserver.disconnect();
      PrioCart.pendingObserver = null;

      PrioCart.setQuantity(1)
        .then((cart) => PrioCart.renderSections(cart?.sections))
        .catch((error) => console.error(error));
    });

    PrioCart.pendingObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  static async post(endpoint, body) {
    const cartUrl = window.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`Prio cart request failed: ${endpoint}`);

    return response.json();
  }

  static isGiftItem(item) {
    return Object.keys(item.properties || {}).some((key) => key.includes('gift_product'));
  }

  static isPrioItem(item) {
    const settings = PrioCart.settings;
    return Boolean(settings) && item.product_id === settings.productId;
  }

  static hasOnlyAutoItems(cart) {
    if (!PrioCart.isRunning()) return false;

    const items = cart?.items;
    if (!Array.isArray(items) || items.length === 0) return false;

    return items.every((item) => PrioCart.isGiftItem(item) || PrioCart.isPrioItem(item));
  }

  static parseSection(html, selector) {
    return html ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  }

  static renderSections(renderedSections) {
    if (!renderedSections) return;

    PrioCart.sections.forEach(({ id, source, target }) => {
      const parsed = PrioCart.parseSection(renderedSections[id], source);
      if (parsed) target.innerHTML = parsed.innerHTML;
    });
  }

  static setPending(pending) {
    document.querySelectorAll('prio-option').forEach((element) => {
      element.pending = pending;
    });

    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      if (pending) {
        if (button.disabled) button.dataset.prioKeepDisabled = 'true';
        button.disabled = true;
        return;
      }

      if (button.dataset.prioKeepDisabled) {
        delete button.dataset.prioKeepDisabled;
        return;
      }

      button.disabled = false;
    });
  }

  static async setQuantity(quantity) {
    const settings = PrioCart.settings;
    if (!settings) return null;

    return PrioCart.post('update.js', {
      updates: { [settings.variantId]: quantity },
      sections: PrioCart.sections.map((section) => section.id),
      sections_url: window.location.pathname,
    });
  }

  static async ensureInCart(sections, addedVariantId) {
    const settings = PrioCart.settings;
    if (!settings) return null;
    if (addedVariantId && Number(addedVariantId) === settings.variantId) return null;

    if (!PrioCart.isRunning()) {
      PrioCart.addWhenTestStarts();
      return null;
    }

    const body = { updates: { [settings.variantId]: 1 } };

    if (sections?.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    return PrioCart.post('update.js', body);
  }

  static async clearCart() {
    const response = await PrioCart.post('clear.js', {
      sections: PrioCart.sections.map((section) => section.id),
      sections_url: window.location.pathname,
    });

    PrioCart.renderSections(response.sections);

    document
      .querySelectorAll('cart-drawer, cart-items, cart-drawer-items, #main-cart-items, #main-cart-footer')
      .forEach((element) => element.classList.add('is-empty'));

    return response;
  }
}

if (!customElements.get('prio-option')) {
  customElements.define(
    'prio-option',
    class PrioOption extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.prio__switch-input');
        this.detailsToggle = this.querySelector('.prio__details-toggle');
        this.panel = this.querySelector('.prio__panel');

        this.input?.addEventListener('change', this.onToggle.bind(this));
        this.detailsToggle?.addEventListener('click', this.onDetailsToggle.bind(this));

        if (PrioCart.element !== this) return;

        if (this.hasAttribute('data-only-prio-in-cart')) {
          PrioCart.clearCart().catch((error) => console.error(error));
        }
      }

      set pending(value) {
        if (this.input) this.input.disabled = value;
      }

      onDetailsToggle() {
        if (!this.panel) return;

        const isExpanded = this.detailsToggle.getAttribute('aria-expanded') === 'true';
        this.detailsToggle.setAttribute('aria-expanded', String(!isExpanded));
        this.panel.classList.toggle('prio__panel--open', !isExpanded);
      }

      async onToggle() {
        if (!PrioCart.isRunning()) return;

        const shouldAdd = this.input.checked;
        PrioCart.setPending(true);

        try {
          const cart = await PrioCart.setQuantity(shouldAdd ? 1 : 0);

          if (!shouldAdd && PrioCart.hasOnlyAutoItems(cart)) {
            await PrioCart.clearCart();
          } else {
            PrioCart.renderSections(cart?.sections);
          }
        } catch (error) {
          this.input.checked = !shouldAdd;
          console.error(error);
        } finally {
          PrioCart.setPending(false);
        }
      }
    }
  );
}

window.prioCart = PrioCart;
